import cn from "classnames";
import styles from "./UserSelect.module.css";
import { FunctionComponent, useState, useRef, useEffect, useMemo } from "react";
import Select, {
  components,
  OptionProps,
  SingleValueProps,
  MenuListProps,
} from "react-select";
import { Image } from 'react-bootstrap';
import { getColorForUser } from "../../common/UserCard/UserCard";

interface UserOption {
  value: string;
  image: string;
  label: string;
  email: string;
}

export interface IUserCardProps {
  userList: UserOption[];
  className?: string;
}

const CustomOption: FunctionComponent<OptionProps<UserOption>> = (props) => {
  const bgColor = useMemo(
    () => getColorForUser(props.data.label || props.data.email),
    [props.data.label, props.data.email]
  );

  return (
  <components.Option {...props}>
    <div className={styles.userItem}>
      <div className={cn(styles.userImage)}>
        {props.data?.image ?
          <Image src={props.data?.image} alt="Close" />:
            <span 
              className={styles.userFirstLetter} 
              style={{backgroundColor: bgColor}}
            >
              {props.data?.label?.charAt(0).toUpperCase()}
            </span>
        }
      </div>
      <div className={styles.userDetail}>
        <div className={styles.userDetails}>
          {props.data.label}{" "}
          <span className={styles.userEmail}>({props.data.email})</span>
        </div>
      </div>
    </div>
  </components.Option>
);
};

const CustomSingleValue: FunctionComponent<SingleValueProps<UserOption>> = (props) => (
  <components.SingleValue {...props}>
    <div className={styles.getvalue}>
      <span>{props.data.label}</span>
    </div>
  </components.SingleValue>
);

const CustomMenuList: React.FC<MenuListProps<UserOption, false>> = (props) => {
  const [inputValue, setInputValue] = useState("");
  const childrenArray = Array.isArray(props.children) ? props.children : [];

  const filteredChildren = childrenArray.filter((child: any) => {
    if (!child || !child.props) return false;
    const { label, data } = child.props;
    const text = `${label} ${data?.email}`.toLowerCase();
    return text.includes(inputValue.toLowerCase());
  });

  return (
    <components.MenuList {...props}>
      <div className={styles.searchWrap} onMouseDown={(e) => e.stopPropagation()}>
        <input
          type="text"
          placeholder="Search user by name or email..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className={cn('form-control', styles.searchBox)}
          onKeyDown={(e) => { if (["ArrowUp", "ArrowDown", "Enter", "Escape", "Backspace"].includes(e.key)) { e.stopPropagation(); } }} tabIndex={0}
        />
      </div>
      <div className={styles.userItemList}>
        {filteredChildren}
      </div>
    </components.MenuList>
  );
};

const UserSelect: FunctionComponent<IUserCardProps> = ({
  userList,
  className,
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setMenuIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className={cn(styles.userSelectWrap, className)}>
      <Select<UserOption, false>
        options={userList}
        placeholder="Select User"
        isSearchable={false}
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onChange={() => setMenuIsOpen(false)}
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue,
          MenuList: CustomMenuList,
        }}
        closeMenuOnScroll={false}
        closeMenuOnSelect={true}
        className={styles.userSelect}
        menuPosition="absolute"
        classNames={{
            control: ({ isFocused }) => cn(isFocused && styles.controlFocus, styles.control),
            input: () => styles.dropdownInput,
            menu: () => styles.dropDownMenu,
            menuList: () => styles.dropDownMenuList,
            option: ({ isSelected, isFocused }) =>
                cn(
                    styles.dropDownOption,
                    isSelected && styles.dropDownOptionSelected,
                    isFocused && styles.dropDownOptionFocused
                ),
            indicatorSeparator: () => styles.indicatorSeparator,
            dropdownIndicator: () => styles.dropdownIndicator,
            valueContainer: () => styles.valueContainer,
            placeholder: () => styles.placeholder,
        }}
      />
    </div>
  );
};

export default UserSelect;
